import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
    appInnerCardStyle,
    appMutedTextStyle,
    appSectionCardStyle,
    renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";
import { STEP03_BEHAVIORAL_INDICATORS } from "../nurseAssessmentFormConstants";
import { useNurseFormAutoSave } from "../useNurseFormAutoSave";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Step03FormValues {
    // ── Category 1 — Short-term memory ──────────────────────────────────────
    hasShortTermMemoryLoss: boolean;

    // ── Category 2 — Orientation / communication ─────────────────────────────
    isNotOrientedToPlaceOrTime: boolean;
    hasLimitedAbilityToMakeSelfUnderstood: boolean;
    requiresHandsOnAssistanceWithEatingOrDrinking: boolean;

    // ── Category 3 — Behavioral indicators (within last 30 days) ─────────────
    hasDecisionMakingDifficulty: boolean;
    hasHistoryOfPhysicalInjuryToOthers: boolean;
    isCombative: boolean;
    isResistiveToCare: boolean;
    hasSexualActingOut: boolean;
    hasSexualAggression: boolean;
    isAgitatedOrWandersAtNight: boolean;
    eatsNonEdibleObjects: boolean;
    hasInappropriateScreaming: boolean;
    hasGottenLostOutside: boolean;
    hasRepetitiveMovement: boolean;
    leavesStoveOn: boolean;
    isAggressiveOrIntimidating: boolean;
    hasExitSeekingBehaviors: boolean;
    isEasilyIrritated: boolean;
    seeksConstantAttention: boolean;
    hasPatternOfInabilityToControlBehavior: boolean;
    patternOfInabilityToControlBehaviorDetail: string;
    hasUnrealisticFearsOrSuspicions: boolean;
    unrealisticFearsOrSuspicionsDetail: string;
    hasInappropriateToiletingActivity: boolean;
    inappropriateToiletingActivityDetail: string;

    // ── Overall determination ─────────────────────────────────────────────────
    meetsDementiaCriteria: boolean;   // all 3 categories met

    // ── Notes ─────────────────────────────────────────────────────────────────
    dementiaScreeningComments: string;
}

export const DEFAULT_STEP03: Step03FormValues = {
    hasShortTermMemoryLoss: false,
    isNotOrientedToPlaceOrTime: false,
    hasLimitedAbilityToMakeSelfUnderstood: false,
    requiresHandsOnAssistanceWithEatingOrDrinking: false,
    hasDecisionMakingDifficulty: false,
    hasHistoryOfPhysicalInjuryToOthers: false,
    isCombative: false,
    isResistiveToCare: false,
    hasSexualActingOut: false,
    hasSexualAggression: false,
    isAgitatedOrWandersAtNight: false,
    eatsNonEdibleObjects: false,
    hasInappropriateScreaming: false,
    hasGottenLostOutside: false,
    hasRepetitiveMovement: false,
    leavesStoveOn: false,
    isAggressiveOrIntimidating: false,
    hasExitSeekingBehaviors: false,
    isEasilyIrritated: false,
    seeksConstantAttention: false,
    hasPatternOfInabilityToControlBehavior: false,
    patternOfInabilityToControlBehaviorDetail: "",
    hasUnrealisticFearsOrSuspicions: false,
    unrealisticFearsOrSuspicionsDetail: "",
    hasInappropriateToiletingActivity: false,
    inappropriateToiletingActivityDetail: "",
    meetsDementiaCriteria: false,
    dementiaScreeningComments: "",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div className="col-12 md:col-4 mb-2">
        <div className="text-xs font-medium mb-1" style={{ color: "#6b7280" }}>{label}</div>
        <div className="text-sm font-medium" style={{ color: value ? "#111827" : "#9ca3af" }}>{value || "—"}</div>
    </div>
);

const LabeledField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="flex flex-column gap-1 mb-3">
        <label className="font-medium text-sm" style={{ color: "#374151" }}>{label}</label>
        {children}
    </div>
);

const CategoryBadge: React.FC<{ number: number; met: boolean }> = ({ number, met }) => (
    <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        background: met ? "#dcfce7" : "#f1f5f9",
        color: met ? "#15803d" : "#64748b",
        border: `1px solid ${met ? "#86efac" : "#cbd5e1"}`,
        borderRadius: 12, padding: "2px 10px", fontSize: "0.78rem", fontWeight: 700,
        marginLeft: 8,
    }}>
        {met ? <i className="pi pi-check" /> : <i className="pi pi-circle" />}
        {`Cat. ${number}`}
    </span>
);

const formatSelected = (items: Array<string | false | null | undefined>) => {
    const selected = items.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    return selected.length ? selected.join(", ") : undefined;
};

// ── Props ─────────────────────────────────────────────────────────────────────

export interface Step03Props {
    sectionData: Partial<Step03FormValues> | null;
    residentId: string;
    formId: string;
    onSave: (data: Step03FormValues) => Promise<void>;
    onNext: () => void;
    onBack: () => void;
    loading?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

const Step03DementiaScreening: React.FC<Step03Props> = ({
    sectionData, residentId, formId, onSave, onNext, onBack, loading,
}) => {
    const toast = useRef<Toast>(null);
    const [enableEditing, setEnableEditing] = useState<boolean>(false);

    const defaultValues: Step03FormValues = {
        ...DEFAULT_STEP03,
        ...(sectionData || {}),
    };

    const { control, handleSubmit, watch, setValue, reset } = useForm<Step03FormValues>({ defaultValues });

    // Live watchers for reactive UI
    const cat1Met = watch("hasShortTermMemoryLoss");
    const cat2Met =
        watch("isNotOrientedToPlaceOrTime") ||
        watch("hasLimitedAbilityToMakeSelfUnderstood") ||
        watch("requiresHandsOnAssistanceWithEatingOrDrinking");

    const behavioralChecked = STEP03_BEHAVIORAL_INDICATORS.some(([fname]) => watch(fname as keyof Step03FormValues));
    const hasPatternChecked = watch("hasPatternOfInabilityToControlBehavior");
    const hasUnrealisticChecked = watch("hasUnrealisticFearsOrSuspicions");
    const hasToiletingChecked = watch("hasInappropriateToiletingActivity");
    const cat3Met = behavioralChecked || hasPatternChecked || hasUnrealisticChecked || hasToiletingChecked;

    const allThreeMet = cat1Met && cat2Met && cat3Met;

    // Sync meetsDementiaCriteria whenever the three categories change
    React.useEffect(() => {
        setValue("meetsDementiaCriteria", allThreeMet);
    }, [allThreeMet, setValue]);

    // Silent submit — auto-save only
    const onSubmit = async (data: Step03FormValues) => {
        await onSave(data);
    };

    const handleSave = async (data: Step03FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Dementia screening saved successfully.", life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message || "An error occurred while saving.", life: 4000 });
        }
    };

    const handleSaveAndContinue = async (data: Step03FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Dementia screening saved. Moving to next step.", life: 2000 });
            onNext();
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message || "An error occurred while saving.", life: 4000 });
        }
    };

    useNurseFormAutoSave({
        enabled: enableEditing && !!formId,
        baselineData: defaultValues,
        watch,
        handleSubmit,
        onSubmit,
    });

    const values = watch();

    return (
        <>
            <Toast ref={toast} />

            <form onSubmit={handleSubmit(handleSave)}>
                <div className="flex justify-content-center gap-2 mb-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
                {!enableEditing ? (
                    <>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Dementia Specialty Care Screening", "Assessment criteria summary.", "pi pi-chart-bar")}
                            <div className="p-3">
                                <div className="grid mb-3">
                                    <InfoRow label="Meets Dementia Criteria" value={values.meetsDementiaCriteria ? "Yes — All 3 Categories Met" : "No"} />
                                    <InfoRow label="Cat. 1 — Short-term Memory Loss" value={values.hasShortTermMemoryLoss ? "Met" : "Not met"} />
                                    <InfoRow label="Cat. 2 — Orientation / Communication" value={(values.isNotOrientedToPlaceOrTime || values.hasLimitedAbilityToMakeSelfUnderstood || values.requiresHandsOnAssistanceWithEatingOrDrinking) ? "Met" : "Not met"} />
                                    <InfoRow label="Cat. 3 — Behavioral Indicators" value={cat3Met ? "Met" : "Not met"} />
                                    <InfoRow label="Short-term Memory Loss" value={values.hasShortTermMemoryLoss ? "Yes" : "No"} />
                                    <InfoRow label="Orientation / Communication Findings" value={formatSelected([
                                        values.isNotOrientedToPlaceOrTime && "Not oriented to place or time",
                                        values.hasLimitedAbilityToMakeSelfUnderstood && "Limited ability to make self understood",
                                        values.requiresHandsOnAssistanceWithEatingOrDrinking && "Requires hands-on assistance with eating or drinking",
                                    ]) || "None selected"} />
                                    <InfoRow label="Behavioral Findings (Last 30 Days)" value={formatSelected([
                                        values.hasDecisionMakingDifficulty && "Decision-making difficulty",
                                        values.hasHistoryOfPhysicalInjuryToOthers && "History of physical injury to others",
                                        values.isCombative && "Combative",
                                        values.isResistiveToCare && "Resistive to care",
                                        values.hasSexualActingOut && "Sexual acting out",
                                        values.hasSexualAggression && "Sexual aggression",
                                        values.isAgitatedOrWandersAtNight && "Agitated or wanders at night",
                                        values.eatsNonEdibleObjects && "Eats non-edible objects",
                                        values.hasInappropriateScreaming && "Inappropriate screaming",
                                        values.hasGottenLostOutside && "Has gotten lost outside",
                                        values.hasRepetitiveMovement && "Repetitive movement",
                                        values.leavesStoveOn && "Leaves stove on",
                                        values.isAggressiveOrIntimidating && "Aggressive or intimidating",
                                        values.hasExitSeekingBehaviors && "Exit-seeking behaviors",
                                        values.isEasilyIrritated && "Easily irritated",
                                        values.seeksConstantAttention && "Seeks constant attention",
                                        values.hasPatternOfInabilityToControlBehavior && "Pattern of inability to control behavior",
                                        values.hasUnrealisticFearsOrSuspicions && "Unrealistic fears or suspicions",
                                        values.hasInappropriateToiletingActivity && "Inappropriate toileting activity",
                                    ]) || "None selected"} />
                                    <InfoRow label="Behavior Control Details" value={values.patternOfInabilityToControlBehaviorDetail} />
                                    <InfoRow label="Fears / Suspicions Details" value={values.unrealisticFearsOrSuspicionsDetail} />
                                    <InfoRow label="Toileting Activity Details" value={values.inappropriateToiletingActivityDetail} />
                                    <InfoRow label="Comments" value={values.dementiaScreeningComments} />
                                </div>
                            </div>
                        </Card>
                    </>
                ) : (
                    <>
                        {/* ── Regulatory context note ──────────────────────────────── */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader(
                                "Dementia Specialty Care Screening",
                                "Assess whether this individual meets the criteria for placement in a Dementia Specialty Adult Family Home.",
                                "pi pi-chart-bar",
                            )}
                            <div className="p-3">
                                <div style={{ ...appMutedTextStyle, lineHeight: 1.6 }}>
                                    Effective October 1, 1999, this form must be completed along with Resident Assessments{" "}
                                    <strong>unless</strong> the individual is already residing in a Dementia, Developmental Disability,
                                    or Mental Health Specialty Home meeting WAC 388-76-59020 criteria.
                                    <br /><br />
                                    An individual meets the criteria for a Dementia Specialty AFH if{" "}
                                    <strong>ALL THREE</strong> of the following categories apply:
                                </div>

                                {/* Progress summary */}
                                <div style={{
                                    marginTop: "1rem", padding: "0.75rem 1rem",
                                    background: allThreeMet ? "#f0fdf4" : "#fef9ec",
                                    border: `1px solid ${allThreeMet ? "#86efac" : "#fde68a"}`,
                                    borderRadius: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                                }}>
                                    <CategoryBadge number={1} met={!!cat1Met} />
                                    <CategoryBadge number={2} met={cat2Met} />
                                    <CategoryBadge number={3} met={cat3Met} />
                                    <span style={{ marginLeft: "auto", fontWeight: 700, color: allThreeMet ? "#15803d" : "#92400e", fontSize: "0.85rem" }}>
                                        {allThreeMet
                                            ? "✓ Meets all 3 criteria — Dementia Specialty care indicated"
                                            : "Not all criteria met"}
                                    </span>
                                </div>
                            </div>
                        </Card>

                        {/* ── Category 1: Short-term memory ───────────────────────── */}
                        <Card style={{ ...appSectionCardStyle, border: cat1Met ? "1.5px solid #86efac" : undefined }} className="mb-3">
                            {renderSurfaceHeader(
                                "Category 1 — Short-Term Memory",
                                "Must be present for criteria to be met.",
                                "pi pi-bookmark",
                            )}
                            <div className="p-3">
                                <Controller
                                    name="hasShortTermMemoryLoss"
                                    control={control}
                                    render={({ field }) => (
                                        <div className="flex align-items-start gap-3">
                                            <Checkbox
                                                inputId="cat1-memory"
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.checked)}
                                                style={{ marginTop: 2, flexShrink: 0 }}
                                            />
                                            <label htmlFor="cat1-memory" className="cursor-pointer" style={{ color: "#1e293b" }}>
                                                Individual shows evidence of <strong>short-term memory loss</strong>
                                            </label>
                                        </div>
                                    )}
                                />
                            </div>
                        </Card>

                        {/* ── Category 2: Orientation / communication ─────────────── */}
                        <Card style={{ ...appSectionCardStyle, border: cat2Met ? "1.5px solid #86efac" : undefined }} className="mb-3">
                            {renderSurfaceHeader(
                                "Category 2 — Orientation & Communication",
                                "At least ONE of the following must be present.",
                                "pi pi-bookmark",
                            )}
                            <div className="p-3">
                                <div style={appMutedTextStyle} className="mb-3">
                                    Check all that apply. At least one is required for Category 2.
                                </div>
                                <div className="flex flex-column gap-3">
                                    {(
                                        [
                                            ["isNotOrientedToPlaceOrTime", "Is not oriented to place or time"],
                                            ["hasLimitedAbilityToMakeSelfUnderstood", "Has limited ability to make him/herself understood through speech, writing, sign language, or any other method"],
                                            ["requiresHandsOnAssistanceWithEatingOrDrinking", "Requires hands-on assistance with eating or drinking"],
                                        ] as [keyof Step03FormValues, string][]
                                    ).map(([fname, lbl]) => (
                                        <Controller
                                            key={fname}
                                            name={fname}
                                            control={control}
                                            render={({ field }) => (
                                                <div className="flex align-items-start gap-3">
                                                    <Checkbox
                                                        inputId={`cat2-${fname}`}
                                                        checked={!!field.value}
                                                        onChange={(e) => field.onChange(e.checked)}
                                                        style={{ marginTop: 2, flexShrink: 0 }}
                                                    />
                                                    <label htmlFor={`cat2-${fname}`} className="cursor-pointer" style={{ color: "#1e293b" }}>
                                                        {lbl}
                                                    </label>
                                                </div>
                                            )}
                                        />
                                    ))}
                                </div>
                            </div>
                        </Card>

                        {/* ── Category 3: Behavioral indicators ───────────────────── */}
                        <Card style={{ ...appSectionCardStyle, border: cat3Met ? "1.5px solid #86efac" : undefined }} className="mb-3">
                            {renderSurfaceHeader(
                                "Category 3 — Behavioral Indicators (within last 30 days)",
                                "At least ONE of the following must have occurred within the last 30 days.",
                                "pi pi-bookmark",
                            )}
                            <div className="p-3">
                                <div style={appMutedTextStyle} className="mb-3">
                                    Check all that have been exhibited within the last 30 days. At least one is required for Category 3.
                                </div>

                                <div className="grid">
                                    {STEP03_BEHAVIORAL_INDICATORS.map(([fname, lbl]) => (
                                        <div key={fname} className="col-12 md:col-6">
                                            <Controller
                                                name={fname as keyof Step03FormValues}
                                                control={control}
                                                render={({ field }) => (
                                                    <div className="flex align-items-start gap-2 mb-2">
                                                        <Checkbox
                                                            inputId={`cat3-${fname}`}
                                                            checked={!!field.value}
                                                            onChange={(e) => field.onChange(e.checked)}
                                                            style={{ marginTop: 2, flexShrink: 0 }}
                                                        />
                                                        <label htmlFor={`cat3-${fname}`} className="cursor-pointer text-sm" style={{ color: "#1e293b" }}>
                                                            {lbl}
                                                        </label>
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {/* Items with conditional detail text */}
                                <div className="flex flex-column gap-3 mt-2">
                                    {(
                                        [
                                            ["hasPatternOfInabilityToControlBehavior", "patternOfInabilityToControlBehaviorDetail", "Pattern of inability to control own behaviors"],
                                            ["hasUnrealisticFearsOrSuspicions", "unrealisticFearsOrSuspicionsDetail", "Unrealistic fears or suspicions"],
                                            ["hasInappropriateToiletingActivity", "inappropriateToiletingActivityDetail", "Inappropriate toileting activity"],
                                        ] as [keyof Step03FormValues, keyof Step03FormValues, string][]
                                    ).map(([checkField, detailField, lbl]) => {
                                        const isChecked = watch(checkField) as boolean;
                                        return (
                                            <div key={checkField} style={isChecked ? appInnerCardStyle : undefined} className={isChecked ? "p-3" : ""}>
                                                <Controller
                                                    name={checkField}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <div className="flex align-items-start gap-2 mb-2">
                                                            <Checkbox
                                                                inputId={`cat3-${checkField}`}
                                                                checked={!!field.value}
                                                                onChange={(e) => field.onChange(e.checked)}
                                                                style={{ marginTop: 2, flexShrink: 0 }}
                                                            />
                                                            <label htmlFor={`cat3-${checkField}`} className="cursor-pointer text-sm" style={{ color: "#1e293b" }}>
                                                                {lbl}
                                                            </label>
                                                        </div>
                                                    )}
                                                />
                                                {isChecked && (
                                                    <div className="ml-5">
                                                        <LabeledField label="Describe:">
                                                            <Controller
                                                                name={detailField}
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <InputTextarea
                                                                        {...field}
                                                                        value={field.value as string}
                                                                        className="w-full"
                                                                        rows={2}
                                                                        autoResize
                                                                        placeholder="Provide details…"
                                                                    />
                                                                )}
                                                            />
                                                        </LabeledField>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </Card>

                        {/* ── Overall determination ────────────────────────────────── */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Overall Determination", undefined, "pi pi-check-circle")}
                            <div className="p-3">
                                <Controller
                                    name="meetsDementiaCriteria"
                                    control={control}
                                    render={({ field }) => (
                                        <div
                                            style={{
                                                ...appInnerCardStyle,
                                                background: field.value ? "#f0fdf4" : "#f8fafc",
                                                border: `1.5px solid ${field.value ? "#86efac" : "#e2e8f0"}`,
                                            }}
                                            className="p-3"
                                        >
                                            <div className="flex align-items-start gap-3">
                                                <Checkbox
                                                    inputId="meetsDementiaCriteria"
                                                    checked={field.value}
                                                    onChange={(e) => field.onChange(e.checked)}
                                                    style={{ marginTop: 2, flexShrink: 0 }}
                                                />
                                                <label htmlFor="meetsDementiaCriteria" className="cursor-pointer" style={{ color: "#1e293b" }}>
                                                    <span className="font-bold">
                                                        This individual meets the criteria in Categories 1, 2 &amp; 3 above
                                                    </span>{" "}
                                                    and is therefore assessed to need special care in a Dementia Specialty Adult Family Home.
                                                </label>
                                            </div>
                                            {allThreeMet && !field.value && (
                                                <div style={{ ...appMutedTextStyle, fontSize: "0.8rem", marginLeft: "2rem", marginTop: "0.4rem" }}>
                                                    All three categories are marked above — you may confirm this determination by checking the box.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                />

                                <div className="mt-4">
                                    <LabeledField label="Comments / Additional Observations">
                                        <Controller
                                            name="dementiaScreeningComments"
                                            control={control}
                                            render={({ field }) => (
                                                <InputTextarea
                                                    {...field}
                                                    className="w-full"
                                                    rows={3}
                                                    autoResize
                                                    placeholder="Include diagnoses made by a licensed medical professional, history of mental illness, developmental disability, recent surgeries / hospitalizations, etc."
                                                />
                                            )}
                                        />
                                    </LabeledField>
                                </div>
                            </div>
                        </Card>

                    </>
                )}
                <div className="flex justify-content-center gap-2 mt-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
            </form>
        </>
    );
};

export default Step03DementiaScreening;
